import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { FEATURED_VIDEO } from "@/data/site";

export const VideoHub = ({ playing, setPlaying }) => (
  <section id="media" className="relative py-32 sm:py-40 bg-[color:var(--kmg-deep)] grain" data-testid="video-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead
        eyebrow="07 — Media"
        title="Watch the KMG Digi Marketplace film."
        sub="One minute on how farmers and retailers connect, trade and grow on the KMG Digi Platform."
      />

      <Reveal delay={0.1}>
        <div
          className="mt-16 border border-[rgba(111,227,155,0.2)] bg-[color:var(--kmg-forest)] overflow-hidden"
          data-testid={`video-card-${FEATURED_VIDEO.id}`}
        >
          <div className="relative">
            <video
              src={FEATURED_VIDEO.src}
              poster={FEATURED_VIDEO.poster}
              controls
              playsInline
              preload="metadata"
              data-testid="video-inline-player"
              className="w-full aspect-video bg-black"
            />
          </div>
          <div className="p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-7">
            <div>
              <p className="eyebrow">Official film · {FEATURED_VIDEO.duration}</p>
              <h3 className="font-display text-xl sm:text-2xl mt-4 leading-snug">{FEATURED_VIDEO.title}</h3>
              <p className="text-base mt-3 text-[color:var(--kmg-bone-dim)] leading-relaxed max-w-2xl">{FEATURED_VIDEO.blurb}</p>
            </div>
            <button
              onClick={() => setPlaying(FEATURED_VIDEO)}
              data-testid="video-fullscreen-btn"
              className="shrink-0 inline-flex items-center gap-3 rounded-full border border-[color:var(--kmg-lime)] px-7 py-3.5 text-sm text-[color:var(--kmg-lime)] hover:bg-[color:var(--kmg-lime)] hover:text-[color:var(--kmg-forest)]"
              style={{ transition: "background-color .4s ease, color .4s ease" }}
            >
              <Play size={15} /> Watch large
            </button>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <p className="mt-10 font-mono-label text-[10px] uppercase tracking-[0.18em] text-[color:var(--kmg-bone-dim)] opacity-70">
          More KMG Digi videos can be added here — send us the files or YouTube links.
        </p>
      </Reveal>
    </div>

    <AnimatePresence>
      {playing && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="video-modal"
        >
          <div className="absolute inset-0 bg-[#040d09]/90 backdrop-blur-sm" onClick={() => setPlaying(null)} />
          <motion.div
            initial={{ scale: 0.94, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl border border-[rgba(111,227,155,0.25)] bg-[color:var(--kmg-forest)]"
          >
            <button
              onClick={() => setPlaying(null)}
              data-testid="video-modal-close"
              className="absolute -top-4 -right-3 sm:-right-4 z-10 rounded-full bg-[color:var(--kmg-lime)] p-2.5 text-[color:var(--kmg-forest)]"
              aria-label="Close video"
            >
              <X size={16} />
            </button>
            <video
              key={playing.id}
              src={playing.src}
              poster={playing.poster}
              controls
              autoPlay
              playsInline
              data-testid="video-player"
              className="w-full aspect-video bg-black"
            />
            <div className="p-7">
              <p className="eyebrow">Official film · {playing.duration}</p>
              <h3 className="font-display text-xl mt-3">{playing.title}</h3>
              <p className="text-sm mt-2 text-[color:var(--kmg-bone-dim)]">{playing.blurb}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
);
